import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';
import {
  CommonTranslationKeys,
  FileTranslationKeys,
  InspectionTranslationKeys,
  IssueTranslationKeys,
} from 'intl/keys';
import {
  ImageList,
  ImageListItem,
  useMediaQuery,
  useTheme,
  Button,
  Box,
  Menu,
  MenuItem,
  ButtonBase,
  Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { UploadFromDeviceIcon } from './UploadFromDeviceIcon';
import AddIcon from '@mui/icons-material/Add';
import { ChooseFromDocumentationIcon } from './ChooseFromDocumentationIcon';
import { ListImage } from './listImage';
import { sortFilesWithMainImageFirst } from './sortFilesWithMainImageFirst';
import styles, {
  DEFAULT_THUMBNAIL_CARD_SIZE,
  animatedAddTileSx,
  graphicUploaderImageListSx,
  linkedDocumentationAddTileButtonSx,
} from './styles';
import {
  Event,
  GRAPHIC_UPLOADER_GALLERY_MODE,
  GraphicUploaderGalleryMode,
} from './types';
import { Gallery, GalleryAction } from 'components/common/gallery';
import {
  ADD_TILE_ANIMATION_DURATION_MS,
  fileIdSelector,
  filesStringSelector,
  resolveGalleryActions,
  resolveGallerySlideAfterFilesChange,
} from './model';
import { useGraphicUploaderContext } from '.';
import DragDrop from 'components/common/dragDrop';
import { showErrorToastWithMessage } from 'services/toaster';
import {
  makeMainFileStore,
  useStoreMainDisplayedFile,
} from '../gallery/mainDisplayedFile';
import { makeDisplayedFilesStore } from '../gallery/filesStore';
import {
  makeGalleryStatesStore,
  useStoreGalleryState,
} from '../gallery/galleryStateStore';
import { WithDisplayedFiles } from '../../dataProviders/withDisplayedFiles';
import {
  applyDocumentUrlsResultToDisplayedFile,
  makeDownloadFile,
} from './downloadFile';
import {
  getSharedDocumentUrlsRequestCache,
  clearDocumentPreviewCache,
  setSharedDocumentUrlsRequestCacheEntry,
} from './previewUrlsCache';
import { documentService } from 'services/documentService';
import { fileKindService, signedRequestService } from 'services/fileService';
import { identitySelector } from '../gallery/selectors';
import { EditDescriptionDialog } from '../gallery/editDescription';
import {
  useEditDescriptionForm,
  withEditDescriptionForm,
} from '../../dataCreationForms/withEditDescriptionForm';
import { useImportFromDocumentationOptional } from 'components/documentation/ImportFromDocumentationDialog';
import { useFileSelection } from './hooks/useFileSelection';
import { useBulkActions } from './hooks/useBulkActions';
import { FloatingToolbar } from '../FloatingToolbar';

const Presentational: React.FC = () => {
  const GU = useGraphicUploaderContext();
  const {
    filesStore,
    addFile,
    config,
    gallery,
    setGallery,
    drawings,
    classes: customClasses,
  } = GU;
  const mainFileStore = useStoreMainDisplayedFile();
  const galleryStateStore = useStoreGalleryState();

  const fileCache = getSharedDocumentUrlsRequestCache();

  const downloadFile = useMemo(() => {
    return makeDownloadFile(fileIdSelector, fileCache, filesStore);
  }, [filesStore]);

  const thumbnailSize = config.thumbnailSize ?? DEFAULT_THUMBNAIL_CARD_SIZE;

  const inputRef = useRef<HTMLInputElement>(null);
  const classes = styles();
  const intl = useIntl();
  const importContext = useImportFromDocumentationOptional();
  const [addMenuAnchor, setAddMenuAnchor] = React.useState<null | HTMLElement>(
    null
  );

  const clickHiddenInput = (): void => {
    inputRef?.current?.click();
  };

  const ADD_LABEL = formatTranslation(intl, CommonTranslationKeys.add,);

  const ADD_FILES_LABEL = formatTranslation(intl, CommonTranslationKeys.addFiles,);

  const UPLOAD_FROM_DEVICE_LABEL = formatTranslation(intl, IssueTranslationKeys.uploadFromDevice,);

  const CHOOSE_FROM_DOCUMENTATION_LABEL = formatTranslation(intl, CommonTranslationKeys.chooseFromDocumentation,);

  const CHOOSE_FROM_DEVICE_LABEL = formatTranslation(intl, CommonTranslationKeys.chooseFromDevice,);

  const DROP_FILES = formatTranslation(intl, IssueTranslationKeys.previewDropZoneTittle,);

  const handleOpenAddMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAddMenuAnchor(event.currentTarget);
    },
    []
  );

  const handleCloseAddMenu = useCallback(() => {
    setAddMenuAnchor(null);
  }, []);

  const handleUploadFromDevice = useCallback(() => {
    handleCloseAddMenu();
    clickHiddenInput();
  }, [handleCloseAddMenu]);

  const handleChooseFromDocumentation = useCallback(() => {
    handleCloseAddMenu();
    importContext?.openImportDialog();
  }, [handleCloseAddMenu, importContext]);

  const handleLinkedDocumentationAddTileClick = useCallback(() => {
    importContext?.openImportDialog();
  }, [importContext]);

  const handleAttachmentAddTileClick = useCallback(() => {
    clickHiddenInput();
  }, []);

  const { dataQaPrefix, mainImageHandlerRef } = config;
  const galleryMode: GraphicUploaderGalleryMode =
    config.galleryMode ?? GRAPHIC_UPLOADER_GALLERY_MODE.DEFAULT;
  const isIssueAttachmentsMode =
    galleryMode === GRAPHIC_UPLOADER_GALLERY_MODE.ISSUE_ATTACHMENTS;
  const isIssueLinkedDocumentationMode =
    galleryMode === GRAPHIC_UPLOADER_GALLERY_MODE.ISSUE_LINKED_DOCUMENTATION;
  const showDropSnackbar =
    isIssueAttachmentsMode && !config.previewOnly && !config.hideAddFiles;
  const dropSnackbarMessage = !showDropSnackbar
    ? undefined
    : config.attachmentDropTarget === 'inspection'
      ? formatTranslation(
          intl,
          InspectionTranslationKeys.dropFilesToInspectionAttachments
        )
      : formatTranslation(intl, IssueTranslationKeys.dropFilesToUpload);

  const handleDirectoryDropRejected = useCallback((): void => {
    showErrorToastWithMessage(FileTranslationKeys.fileDirectoryAttachmentNotSupported);
  }, []);
  const isSelectableMode =
    isIssueAttachmentsMode || isIssueLinkedDocumentationMode;

  const sectionId = isIssueLinkedDocumentationMode
    ? 'linked-documentation'
    : 'attachments';
  const fileSelection = useFileSelection(sectionId);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const activeGalleryActions = resolveGalleryActions({
    galleryMode,
  });

  const showExportToDocumentation = activeGalleryActions.includes(
    GalleryAction.EXPORT_TO_DOCUMENTATION
  );

  const setFiles = useCallback(() => {}, []);
  const useMainDisplayedFile = useMemo(
    () => makeMainFileStore(mainFileStore),
    [mainFileStore]
  );

  const useDisplayedFiles = useMemo(
    () =>
      makeDisplayedFilesStore(
        filesStore,
        (files) => {
          // Linked documentation actions rely on visible index -> file mapping.
          // Keep original store order in this mode to avoid index drift.
          if (isIssueLinkedDocumentationMode) {
            return files;
          }

          if (!mainImageHandlerRef) return files;
          const mainImageId =
            mainImageHandlerRef.current.getMainImageStore().get();
          return sortFilesWithMainImageFirst(files, mainImageId);
        },
        mainImageHandlerRef
      ),
    [filesStore, isIssueLinkedDocumentationMode, mainImageHandlerRef]
  );

  const [filesString] = useDisplayedFiles(filesStringSelector);

  const fileKeys = filesString ? filesString.split(',') : [];
  const fileCount = fileKeys.length;

  const showFiles = Boolean(filesString);
  const hasFilesForSizing = showFiles || Boolean(config.hasFiles);

  const shouldUseCompactAddTile = isIssueLinkedDocumentationMode
    ? !showFiles && !config.isLoadingFiles
    : !hasFilesForSizing;

  const addTileLargeIconSize = Math.max(
    24,
    Math.min(56, Math.round(thumbnailSize * 0.55))
  );

  const addTileCompactIconSize = Math.max(
    20,
    Math.min(32, Math.round(thumbnailSize * 0.35))
  );

  const [shouldAnimateAddTile, setShouldAnimateAddTile] = useState(false);

  const prevFileCountRef = useRef(fileCount);

  useEffect(() => {
    if (isIssueLinkedDocumentationMode) {
      prevFileCountRef.current = fileCount;
      setShouldAnimateAddTile(false);
      return;
    }

    const isFirstFileAdded = prevFileCountRef.current === 0 && fileCount > 0;
    if (isFirstFileAdded) {
      setShouldAnimateAddTile(true);
      const timeoutId = window.setTimeout(() => {
        setShouldAnimateAddTile(false);
      }, ADD_TILE_ANIMATION_DURATION_MS);
      prevFileCountRef.current = fileCount;
      return () => window.clearTimeout(timeoutId);
    }

    prevFileCountRef.current = fileCount;
    setShouldAnimateAddTile(false);
  }, [fileCount, isIssueLinkedDocumentationMode]);

  const showLinkedDocumentationAddTile = Boolean(
    isIssueLinkedDocumentationMode && importContext
  );

  const showAttachmentAddTile = Boolean(
    !isIssueLinkedDocumentationMode &&
      config.showAddTileOnly &&
      !config.previewOnly &&
      !config.hideAddFiles
  );

  const showImageList =
    showFiles || showLinkedDocumentationAddTile || showAttachmentAddTile;

  const showTopAddControls =
    !config.previewOnly && !config.hideAddFiles && !showAttachmentAddTile;

  const enableAttachmentTileDropZone =
    isIssueAttachmentsMode && !config.previewOnly && !config.hideAddFiles;
  const imageListSx = enableAttachmentTileDropZone
    ? {
        ...graphicUploaderImageListSx,
        margin: 0,
      }
    : graphicUploaderImageListSx;
  // Tiles have a fixed size, so the column count follows the container width
  // (wrapping to the next row) instead of the viewport breakpoint below; with
  // a fixed count, tiles in a narrow column overlapped and got clipped.
  // `ImageList` writes `repeat(cols, 1fr)` as an inline style and merges the
  // `style` prop over it, so the override goes there.
  const imageListStyle = useMemo(
    () => ({
      gridTemplateColumns: `repeat(auto-fill, minmax(${thumbnailSize}px, 1fr))`,
    }),
    [thumbnailSize]
  );

  const [files] = useDisplayedFiles(identitySelector);

  // `gallery.slide` is a plain numeric index, but `files` (this sorted,
  // main-image-first view) can reorder or change length independently of
  // user action (new upload prepended, main-image resort, server sync).
  // Without re-resolving the slide by the file's own identity, the open
  // slide silently drifts to a different file — or past the end of the
  // array, landing on `undefined` and blanking the gallery — whenever that
  // happens while the gallery is open.
  const previousFilesRef = useRef(files);
  useEffect(() => {
    const previousFiles = previousFilesRef.current;
    previousFilesRef.current = files;

    if (!gallery.open || previousFiles === files) {
      return;
    }

    const nextSlide = resolveGallerySlideAfterFilesChange(
      previousFiles,
      files,
      gallery.slide
    );

    if (nextSlide !== gallery.slide) {
      setGallery({ open: gallery.open, slide: nextSlide });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const refreshPreview = useCallback(async (): Promise<boolean> => {
    const file = files[gallery.slide];
    if (!file || !file.localId) {
      return false;
    }

    clearDocumentPreviewCache(file);

    try {
      const firstResultPromise = documentService.resolveDocumentUrlsWithRetry(
        file.localId,
        undefined,
        undefined,
        true
      );
      setSharedDocumentUrlsRequestCacheEntry(
        file.localId,
        firstResultPromise
      );
      const firstResult = await firstResultPromise;

      setSharedDocumentUrlsRequestCacheEntry(
        file.localId,
        Promise.resolve(firstResult)
      );

      const latestFile =
        filesStore.get().find((entry) => entry.localId === file.localId) ??
        file;
      await applyDocumentUrlsResultToDisplayedFile(
        latestFile,
        firstResult,
        filesStore,
        fileIdSelector,
        { forceApply: true }
      );

      const refreshedFile = filesStore
        .get()
        .find((entry) => entry.localId === file.localId);

      if (
        refreshedFile &&
        !fileKindService.isPendingConversionPdf(refreshedFile)
      ) {
        return true;
      }

      return Boolean(firstResult.documentUrls.imageSignedRequest);
    } catch {
      return false;
    }
  }, [downloadFile, files, filesStore, gallery.slide]);

  useEffect(() => {
    if (
      fileSelection.focusedId !== null &&
      fileSelection.focusedId >= fileCount
    ) {
      fileSelection.setFocusedId(fileCount > 0 ? fileCount - 1 : null);
    }
  }, [fileCount, fileSelection.focusedId, fileSelection.setFocusedId]);

  const bulkActions = useBulkActions({
    selectedIds: fileSelection.selectedIds,
    files,
    drawings,
    removeFile: GU.removeFile,
    clearSelection: fileSelection.clearSelection,
    galleryMode,
    previewOnly: config.previewOnly,
  });

  useEffect(() => {
    mainFileStore.set(files[gallery.slide]);
  }, [mainFileStore, files, gallery.slide]);

  useEffect(() => {
    if (!gallery.open) {
      return;
    }

    const file = files[gallery.slide];
    if (!file) {
      return;
    }

    if (
      file.loading === false &&
      signedRequestService.hasResolvedDownloadSource(file) &&
      fileKindService.hasUsablePreviewSource(file)
    ) {
      return;
    }

    downloadFile(file);
  }, [downloadFile, files, gallery.open, gallery.slide]);

  useEffect(() => {
    galleryStateStore.set(gallery);
  }, [galleryStateStore, gallery]);

  const useGalleryState = useMemo(
    () => makeGalleryStatesStore(galleryStateStore),
    [galleryStateStore]
  );

  const { openDialog } = useEditDescriptionForm();

  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const cols = isXl ? 8 : isLg ? 6 : isMd ? 4 : isSm ? 3 : 2;

  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isSelectableMode) return;
      // Arrow up/down must move by the columns actually rendered (auto-fill).
      const listElement = gridContainerRef.current?.querySelector(
        '.MuiImageList-root'
      );
      const renderedCols = listElement
        ? window
            .getComputedStyle(listElement)
            .gridTemplateColumns.split(' ')
            .filter(Boolean).length
        : 0;
      fileSelection.handleKeyDown(e, fileCount, renderedCols || cols);
    },
    [isSelectableMode, fileSelection.handleKeyDown, fileCount, cols]
  );

  const imageListContent = (
    <Box
      ref={gridContainerRef}
      tabIndex={isSelectableMode ? 0 : undefined}
      onKeyDown={isSelectableMode ? handleGridKeyDown : undefined}
      sx={{ outline: 'none' }}
    >
      <ImageList cols={cols} gap={16} sx={imageListSx} style={imageListStyle}>
        {showLinkedDocumentationAddTile && (
          <ImageListItem
            key='linked-documentation-add-tile'
            sx={animatedAddTileSx(thumbnailSize, shouldUseCompactAddTile, true)}
          >
            <Tooltip title={CHOOSE_FROM_DOCUMENTATION_LABEL}>
              <ButtonBase
                onClick={handleLinkedDocumentationAddTileClick}
                data-qa={`${dataQaPrefix}graphic-uploader-add-tile-button`}
                aria-label={CHOOSE_FROM_DOCUMENTATION_LABEL}
                sx={linkedDocumentationAddTileButtonSx}
              >
                <ChooseFromDocumentationIcon
                  sx={{
                    fontSize: shouldUseCompactAddTile
                      ? addTileCompactIconSize
                      : addTileLargeIconSize,
                  }}
                />
              </ButtonBase>
            </Tooltip>
          </ImageListItem>
        )}
        {showAttachmentAddTile && (
          <ImageListItem
            key='attachment-add-tile'
            sx={animatedAddTileSx(
              thumbnailSize,
              shouldUseCompactAddTile,
              shouldAnimateAddTile
            )}
          >
            <Tooltip title={CHOOSE_FROM_DEVICE_LABEL}>
              <ButtonBase
                onClick={handleAttachmentAddTileClick}
                data-qa={`${dataQaPrefix}graphic-uploader-upload-tile-button`}
                aria-label={UPLOAD_FROM_DEVICE_LABEL}
                sx={linkedDocumentationAddTileButtonSx}
              >
                <UploadFromDeviceIcon
                  sx={{
                    fontSize: shouldUseCompactAddTile
                      ? addTileCompactIconSize
                      : addTileLargeIconSize,
                  }}
                />
              </ButtonBase>
            </Tooltip>
          </ImageListItem>
        )}
        {fileKeys.map((fileString, id) => (
          <ListImage
            key={`${fileString}-${id}`}
            id={id}
            drawing={drawings[id]}
            thumbnailSize={thumbnailSize}
            onClick={(): void => {
              fileSelection.clearSelection();
              setGallery({ open: true, slide: id });
            }}
            hideRemove={false}
            hideEdit={isIssueLinkedDocumentationMode}
            hideExportToDocumentation={!showExportToDocumentation}
            selected={isSelectableMode ? fileSelection.isSelected(id) : undefined}
            focused={isSelectableMode ? fileSelection.isFocused(id) : undefined}
            onSelect={isSelectableMode ? fileSelection.handleSelect : undefined}
          />
        ))}
      </ImageList>
    </Box>
  );

  return (
    <>
      {showTopAddControls && (
        <Box className={customClasses?.buttonContainer} sx={{ width: '100%' }}>
          <DragDrop
            handleInput={addFile}
            data-qa={`${dataQaPrefix}graphic-uploader-drag-drop-zone`}
            snackbarMessage={dropSnackbarMessage}
            onDirectoryDropRejected={handleDirectoryDropRejected}
          >
            <Box sx={{ flex: '0 1 auto', marginLeft: '16px' }}>
              {config.addMenuActions ? (
                <>
                  <Button
                    variant='contained'
                    onClick={handleOpenAddMenu}
                    startIcon={<AddIcon />}
                    data-qa={`${dataQaPrefix}graphic-uploader-add-files-button`}
                  >
                    +{ADD_LABEL}
                  </Button>
                  <Menu
                    anchorEl={addMenuAnchor}
                    open={Boolean(addMenuAnchor)}
                    onClose={handleCloseAddMenu}
                  >
                    <MenuItem onClick={handleUploadFromDevice}>
                      <CloudUploadIcon fontSize='small' sx={{ mr: 1 }} />
                      {UPLOAD_FROM_DEVICE_LABEL}
                    </MenuItem>
                    {importContext && (
                      <MenuItem onClick={handleChooseFromDocumentation}>
                        {CHOOSE_FROM_DOCUMENTATION_LABEL}
                      </MenuItem>
                    )}
                  </Menu>
                </>
              ) : (
                <Button
                  variant='contained'
                  onClick={clickHiddenInput}
                  startIcon={<CloudUploadIcon />}
                  data-qa={`${dataQaPrefix}graphic-uploader-add-files-button`}
                >
                  {ADD_FILES_LABEL}
                </Button>
              )}
            </Box>
            <Box sx={{ flex: '1 1 fit-content', margin: '12px 0 12px 16px' }}>
              {DROP_FILES}
            </Box>
          </DragDrop>
        </Box>
      )}
      <input
        ref={inputRef}
        type='file'
        onChange={(e: Event): void => {
          if (e.target.files) {
            addFile(Object.values(e.target.files));
          }
          e.target.value = '';
        }}
        accept={config?.file_types?.join()}
        className={classes.fileInput}
        multiple
      />
      <WithDisplayedFiles
        filesStore={filesStore}
        useDisplayedFiles={useDisplayedFiles}
        downloadFile={downloadFile}
      >
        <Gallery
          addDrawing={GU.addDrawing}
          changeGallery={GU.changeGallery}
          drawings={GU.drawings}
          editFile={GU.editFile}
          removeFile={GU.removeFile}
          rotateFile={GU.rotateFile}
          setGallery={GU.setGallery}
          setFiles={setFiles}
          preview={Boolean(config.previewOnly)}
          disableMiniatures={false}
          showActions={activeGalleryActions}
          useDisplayedFiles={useDisplayedFiles}
          useMainDisplayedFile={useMainDisplayedFile}
          useGalleryState={useGalleryState}
          downloadFile={downloadFile}
          refreshPreview={refreshPreview}
          openEditComponent={openDialog}
          mainImageHandlerRef={mainImageHandlerRef}
          fetchShareableUrl={config.fetchShareableUrl}
        />

        <EditDescriptionDialog
          editFile={GU.editFile}
          useGalleryState={useGalleryState}
          mainImageHandlerRef={mainImageHandlerRef}
        />

        {showImageList && (
          <>
            {isSelectableMode && (
              <FloatingToolbar
                selectedCount={fileSelection.selectedCount}
                actions={bulkActions}
                onClearSelection={fileSelection.clearSelection}
              />
            )}
            {enableAttachmentTileDropZone ? (
              <DragDrop
                handleInput={addFile}
                data-qa={`${dataQaPrefix}graphic-uploader-attachment-drop-zone`}
                variant='subtle'
                snackbarMessage={dropSnackbarMessage}
                onDirectoryDropRejected={handleDirectoryDropRejected}
              >
                {imageListContent}
              </DragDrop>
            ) : (
              imageListContent
            )}
          </>
        )}
      </WithDisplayedFiles>
    </>
  );
};

export default withEditDescriptionForm(Presentational);
